import './chartArea.less'
import ChartTooltip from '../ChartTooltip'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import React, { ReactElement } from 'react'
import classNames from 'classnames'

export type ChartBarDataItem = {
  name: string
  value: string | number
}

interface IProps {
  data: ChartBarDataItem[]
  areaColor?: string
  diamondDot?: boolean
  height?: number
  print?: boolean
  cartesianGridHorrizontal?: boolean
  cartesianGridVertical?: boolean
  plainAreaColor?: string
  strokeColor?: string
  className?: string
  showAxisLine?: boolean
  linear?: boolean
  tickFontSize?: { fontSize: string }
  tickFormatter?: { fontSize: string; fill?: string }
  yLabelFormatter?: (value: string) => string
  onClick?: () => void
  renderLegend?: () => ReactElement
  isAnimationActive?: boolean
  yAxisAllowDecimals?: boolean
}

export const DiamondDot = (props) => {
  const { cx, cy, payload } = props

  return payload.value === null ? (
    <></>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      x={cx - 7}
      y={cy - 7}
    >
      <rect
        x="7.07129"
        y="0.707107"
        width="9"
        height="9"
        transform="rotate(45 7.07129 0.707107)"
        fill="#6941C6"
        stroke="white"
      />
    </svg>
  )
}

const ChartArea = (props: IProps): React.ReactElement => {
  const {
    data,
    height = 150,
    print = false,
    linear,
    areaColor = '#82ca9d',
    plainAreaColor,
    strokeColor,
    cartesianGridHorrizontal = false,
    cartesianGridVertical = false,
    showAxisLine = false,
    diamondDot = false,
    className = '',
    tickFontSize = { fontSize: '0.72rem' },
    tickFormatter,
    yLabelFormatter,
    renderLegend,
    onClick,
    isAnimationActive,
    yAxisAllowDecimals = true
  } = props
  const tickFormat = tickFormatter ? tickFormatter : tickFontSize
  return (
    <div
      className={classNames('chart-area-simple custom-scrollbar', { [className]: !!className })}
      onClick={onClick}
      data-test-id="chart-area"
      {...(print ? { style: { width: '1100px' } } : {})}
    >
      <ResponsiveContainer
        {...(print ? { width: '99%' } : {})}
        height={height}
        className={`custom-scrollbar ${className} responsive-container`}
      >
        <AreaChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          barSize={20} // height of the bars
          {...(isAnimationActive ? { isAnimationActive } : {})}
        >
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={areaColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={cartesianGridVertical} horizontal={cartesianGridHorrizontal} />
          <XAxis
            axisLine={showAxisLine} // do not show bottom axis line
            dataKey="name" // data key to render names
            interval="preserveStartEnd"
            tick={tickFormat} // label styles
          />
          <YAxis
            axisLine={showAxisLine}
            {...(yLabelFormatter ? { tickFormatter: yLabelFormatter } : {})}
            tick={tickFormat} // label styles
            interval="preserveStartEnd"
            allowDecimals={yAxisAllowDecimals}
          />
          <Area
            type={linear ? 'linear' : 'monotone'}
            dataKey="value"
            stroke={strokeColor || areaColor}
            fillOpacity={1}
            fill={plainAreaColor || 'url(#colorValue)'}
            {...(diamondDot ? { dot: <DiamondDot /> } : {})}
            strokeWidth={2}
            cursor={onClick ? 'pointer' : 'default'}
          />
          <Tooltip content={<ChartTooltip />} />
          {renderLegend && <Legend height={50} content={renderLegend} />}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default ChartArea
